import { useApi } from "@platform/api";
import {
  useMutation,
  useQuery,
  UseMutationResult,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { currencyFormatter } from "@platform/fe-utils";

import {
  Baseline,
  BaselineDetail,
  BonusForecastChartApi,
  BonusYearSettings,
  BrokerSummary,
  BrokerSummarySavePayload,
  CarrierBonusSummaryApi,
  CarrierRuleSelection,
  CarrierSettingsMap,
  LocCombinedApi,
  Scenario,
  ScenarioDetailRow,
  ScenarioEdit,
  ScenarioEditInput,
  ScenarioEditType,
  ScenarioLOC,
  ScenarioRowStatus,
  ScenarioSummary,
} from "../pages/BBE/types";
import {
  CARRIER_NAME,
  CarrierKey,
  CarrierBrokerSummary,
} from "../pages/BBE/constants";

import useToastNotificationService from "./useToastNotificationService";

const BBE_CARRIERS_PATH = "/bbe/v1/carriers";

const getBrokerSummaryRuleLabel = (summary: BrokerSummary): string =>
  summary.carrierRuleTitle ??
  summary.carrierRuleId ??
  summary.trancheCode ??
  "";

export function useBrokerBonus(
  brokerId: string,
): UseQueryResult<BonusForecastChartApi[], Error> {
  const axios = useApi();

  return useQuery<BonusForecastChartApi[], Error>({
    queryKey: ["bonusForecastCharts", brokerId],
    queryFn: () =>
      axios
        .get<
          BonusForecastChartApi[]
        >(`/bbe/v1/broker/${brokerId}/bonus-forecast-charts`)
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });
}

export function useBrokerHeaderInfo(brokerId: string): {
  data: { estimatedBonus: string; bonusGoal: string; totalBobPremium: string };
  loading: boolean;
} {
  const { data, isLoading } = useBrokerBonus(brokerId);
  const currentBroker =
    data?.filter(
      (d) =>
        d.broker_id === brokerId &&
        d.chart_type === "card" &&
        d.carrier_name === CARRIER_NAME.estimatedBonus,
    ) ?? [];
  const cardData = data?.filter(
    (item: BonusForecastChartApi) => item.chart_type === "card",
  );

  const metrics: Record<string, number | undefined> = {};
  cardData?.forEach((item: BonusForecastChartApi) => {
    if (typeof item.value === "number" && Number.isFinite(item.value)) {
      metrics[item.carrier_name] = item.value;
    }
  });

  const bonusGoalValue = metrics["Bonus Goal"];
  const bonusGoal = currencyFormatter(bonusGoalValue);

  const totalBobPremiumValue = metrics["Total BOB Premium"];
  const totalBobPremium = currencyFormatter(totalBobPremiumValue);

  const estimatedValue = currentBroker?.[0]?.value;
  const estimatedBonus = currencyFormatter(estimatedValue);
  return {
    data: { estimatedBonus, bonusGoal, totalBobPremium },
    loading: isLoading,
  };
}

export function useCarrierBonus(brokerId: string): {
  rowCount: number;
  rowData: CarrierBonusSummaryApi[];
  loading: boolean;
} {
  const axios = useApi();

  const { data, isLoading } = useQuery<CarrierBonusSummaryApi[]>({
    queryKey: ["carrierBonusSummary", brokerId],
    queryFn: () =>
      axios
        .get<
          CarrierBonusSummaryApi[]
        >(`/bbe/v1/broker/${brokerId}/carrier-bonus-summary`)
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });

  return {
    rowCount: data?.length ?? 0,
    rowData: data ?? [],
    loading: isLoading,
  };
}

export function useCarrierBonusByCarrierIdQuery(
  brokerId: string,
  carrierId: string,
): UseQueryResult<Partial<CarrierBonusSummaryApi>, Error> {
  const axios = useApi();

  return useQuery<CarrierBonusSummaryApi, Error>({
    queryKey: ["carrierBonus", brokerId, carrierId],
    queryFn: async () =>
      axios
        .get<CarrierBonusSummaryApi>(
          `/bbe/v1/broker/${brokerId}/carrier-bonus-summary`,
          { params: { carrier_id: carrierId } },
        )
        .then((res) => res.data),
    enabled: Boolean(brokerId && carrierId),
  });
}

export function useBrokerSummaryWithCarrierQuery(
  brokerId: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<CarrierBrokerSummary[], Error> {
  const axios = useApi();
  return useQuery<CarrierBrokerSummary[], Error>({
    queryKey: ["brokerSummaryWithCarrier", brokerId],
    queryFn: () =>
      axios
        .get<CarrierBrokerSummary[]>(`/bbe/v1/broker-summaries/sf/${brokerId}`)
        .then((res) => {
          const carrierIds = res.data.map((r) => r.carrierId);
          if (carrierIds.length === 0) {
            return {
              carriers: { carriers: {} as Record<string, string> },
              res,
            };
          }
          return axios
            .get<{ carriers: Record<string, string> }>(BBE_CARRIERS_PATH, {
              params: { carrier_ids: carrierIds.join(",") },
            })
            .then((carriersRes) => ({
              carriers: carriersRes.data,
              res,
            }));
        })
        .then(({ carriers, res }) =>
          res.data
            // sorting on the FE side is implemented (for now) due to us having to call two separate
            // calls primarily to retrieve carrier's name
            .map((r) => ({
              ...r,
              carrierName:
                (r.carrierName || carriers.carriers[r.carrierId]) ?? "",
              brokerSummaries: [...r.brokerSummaries].sort((a, b) =>
                getBrokerSummaryRuleLabel(a).localeCompare(
                  getBrokerSummaryRuleLabel(b),
                ),
              ),
            }))
            .sort((a, b) => a.carrierName.localeCompare(b.carrierName)),
        ),
    enabled: Boolean(brokerId) && enabled,
  });
}

export function useSaveBrokerSummaryMutation(
  sfBrokerId: string,
  brokerId: string,
): UseMutationResult<BrokerSummary, Error, BrokerSummarySavePayload> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<BrokerSummary, Error, BrokerSummarySavePayload>({
    mutationFn: (options: BrokerSummarySavePayload): Promise<BrokerSummary> => {
      const body: Partial<BrokerSummary> = {
        sfBrokerId,
        sfCarrierId: options.sfCarrierId,
        beginningBookSize: options.beginningBookSize,
        beginningLoc: options.beginningLoc,
        previousYearBonus: options.previousYearBonus,
        bonusGoal: options.bonusGoal,
        carrierRuleId: options.carrierRuleId,
        trancheCode: options.trancheCode,
        isCurrent: options.isCurrent,
      };

      return axiosInstance
        .put<BrokerSummary>(`/bbe/v1/broker-summaries/${brokerId}`, body)
        .then((res) => res.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brokerSummaryWithCarrier", brokerId],
      });
    },
  });
}

export function useCarrierRuleSelectionQuery({
  enabled = true,
}: { enabled?: boolean } = {}): UseQueryResult<CarrierRuleSelection[], Error> {
  const axios = useApi();

  return useQuery<CarrierRuleSelection[]>({
    queryKey: ["carrierRuleSelections"],
    queryFn: () =>
      axios
        .get<CarrierRuleSelection[]>("/bbe/v1/carrier-rules")
        .then((res) => res.data),
    enabled,
  });
}

export function useCurrentBonusYearSettingsQuery<T extends CarrierKey>({
  carrierType,
  currentRuleId,
  bonusYear,
}: {
  carrierType: T;
  currentRuleId?: string;
  bonusYear?: number;
}): Omit<UseQueryResult<BonusYearSettings, Error>, "data"> & {
  data: CarrierSettingsMap[T] | undefined;
} {
  const axios = useApi();

  const { data, ...response } = useQuery<BonusYearSettings>({
    queryKey: ["bonusYearSettings", bonusYear],
    queryFn: () =>
      axios
        .get<BonusYearSettings>("/bbe/v1/bonus-year-settings", {
          params: { bonusYear },
        })
        .then((res) => res.data),
  });

  const normalizeRuleKey = (value?: string): string =>
    (value ?? "").trim().toUpperCase();

  const formattedData: CarrierSettingsMap[T] | undefined =
    carrierType === "lincoln"
      ? Array.isArray(data?.lincoln)
        ? (data.lincoln.find(
            (s) =>
              normalizeRuleKey(s.ruleId ?? s.trancheCode) ===
              normalizeRuleKey(currentRuleId),
          ) ?? data.lincoln[0])
        : data?.lincoln
      : (data?.[carrierType] as CarrierSettingsMap[T]);

  return { ...response, data: formattedData };
}

export function useBrokersQuery(brokerId: string): UseQueryResult<
  {
    groups: {
      groupName: string;
      groupNumber: string;
    }[];
  },
  Error
> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "brokers", brokerId],
    queryFn: () =>
      axios
        .get<{
          groups: {
            groupName: string;
            groupNumber: string;
          }[];
        }>(`/bbe/v1/brokers/${brokerId}/groups`)
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });
}

export function useLocCombinedQuery(
  brokerId: string,
  carrier?: string,
): UseQueryResult<LocCombinedApi[], Error> {
  const axios = useApi();

  return useQuery({
    queryKey: ["locCombined", brokerId, carrier],
    queryFn: () =>
      axios
        .get<LocCombinedApi[]>(`/bbe/v1/broker/${brokerId}/loc-combined`, {
          params: { carrier },
        })
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });
}

export function useBaselinesQuery(
  sfBrokerId: string,
): UseQueryResult<{ baselines: Baseline[]; count: number }, Error> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "baselines", sfBrokerId],
    queryFn: () =>
      axios
        .get<{
          baselines: Baseline[];
          count: number;
        }>(`/bbe/v1/baselines`, { params: { sfBrokerId } })
        .then((res) => res.data),
    enabled: Boolean(sfBrokerId),
  });
}

export function useBaselineDetailQuery(
  baselineId: string,
): UseQueryResult<BaselineDetail, Error> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "baselineDetail", baselineId],
    queryFn: () =>
      axios
        .get<BaselineDetail>(`/bbe/v1/baselines/${baselineId}`)
        .then((res) => res.data),
    enabled: Boolean(baselineId),
  });
}

export function useScenariosSummaryQuery(
  baselineId: string,
): UseQueryResult<{ scenarios: ScenarioSummary[]; count: number }, Error> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "scenariosSummary", baselineId],
    queryFn: () =>
      axios
        .get<{ scenarios: ScenarioSummary[]; count: number }>(
          `/bbe/v1/ui/scenario-modeling-summary`,
          {
            params: { baselineId },
          },
        )
        .then((res) => res.data),
    enabled: Boolean(baselineId),
  });
}

const ROW_STATUS_BY_EDIT_TYPE: Partial<
  Record<ScenarioEditType, ScenarioRowStatus>
> = {
  [ScenarioEditType.Terminate]: ScenarioRowStatus.Terminated,
  [ScenarioEditType.LostBOR]: ScenarioRowStatus.LostBOR,
  [ScenarioEditType.Migrate]: ScenarioRowStatus.Terminated,
};

export function useScenarioDetailsQuery(scenarioId: string): UseQueryResult<
  {
    scenario: Scenario;
    edits: ScenarioEdit[];
    rows: ScenarioDetailRow[];
  },
  Error
> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "scenarioDetails", scenarioId],
    queryFn: () =>
      axios
        .get<{
          scenario: Scenario;
          edits: ScenarioEdit[];
          newLocs: ScenarioLOC[];
        }>(`/bbe/v1/scenarios/${scenarioId}`)
        .then((res) => {
          const scenarioDetails = res.data;
          const baselineId = scenarioDetails.scenario.baselineId;

          return axios
            .get<BaselineDetail>(`/bbe/v1/baselines/${baselineId}`)
            .then((baselineRes) => {
              const { edits, newLocs, scenario } = scenarioDetails;
              const locs = baselineRes.data.locs;

              const editBySourceLocId = new Map<string, ScenarioEdit>(
                edits
                  .filter((e) => e.sourceBaselineLocId)
                  .map((e) => [e.sourceBaselineLocId as string, e]),
              );

              const editById = new Map<string, ScenarioEdit>(
                edits.map((edit) => [edit.id, edit]),
              );

              const baselineRows = locs.map(({ baselineId: _, ...loc }) => {
                const edit = editBySourceLocId.get(loc.id);
                const status = edit
                  ? ROW_STATUS_BY_EDIT_TYPE[edit.editType]
                  : undefined;
                const statusOverride = status
                  ? { status, statusDate: edit!.dateOfChange }
                  : {};

                return {
                  ...loc,
                  scenarioId: scenario.id,
                  editId: edit?.id ?? null,
                  scenarioChange: edit?.editType ?? null,
                  ...statusOverride,
                  ...(edit?.editType === ScenarioEditType.Migrate
                    ? {
                        // Source row keeps its original carrier (it represents the
                        // terminated original policy); only expose the destination
                        // name so the Scenario Change column can read "Migrated to …".
                        migrateToCarrierName: edit.migrateToCarrierName ?? null,
                      }
                    : {}),
                };
              });

              const locById = new Map(locs.map((l) => [l.id, l]));

              // NewLocs (ADD_NEW and MIGRATE) with scenarioChange
              const newLocRows = newLocs.flatMap((loc) => {
                const edit = loc.editId ? editById.get(loc.editId) : undefined;
                if (
                  edit?.editType !== ScenarioEditType.AddNew &&
                  edit?.editType !== ScenarioEditType.Migrate
                ) {
                  return [];
                }
                const migrateFromCarrierName =
                  edit.editType === ScenarioEditType.Migrate &&
                  edit.sourceBaselineLocId
                    ? (locById.get(edit.sourceBaselineLocId)?.carrierName ??
                      null)
                    : undefined;
                return [
                  {
                    ...loc,
                    scenarioChange: edit.editType,
                    ...(edit.editType === ScenarioEditType.Migrate
                      ? { statusDate: null }
                      : {}),
                    ...(migrateFromCarrierName !== undefined
                      ? { migrateFromCarrierName }
                      : {}),
                  },
                ];
              });

              // Colocate MIGRATE pairs: insert each newLoc immediately after its source row.
              const migrateNewLocByEditId = new Map(
                newLocRows
                  .filter((r) => r.scenarioChange === ScenarioEditType.Migrate)
                  .map((r) => [r.editId, r]),
              );
              const addNewRows = newLocRows.filter(
                (r) => r.scenarioChange !== ScenarioEditType.Migrate,
              );
              const rows: ScenarioDetailRow[] = [
                ...baselineRows.flatMap((row) =>
                  row.editId && migrateNewLocByEditId.has(row.editId)
                    ? [row, migrateNewLocByEditId.get(row.editId)!]
                    : [row],
                ),
                ...addNewRows,
              ];

              return { scenario, edits, rows };
            });
        }),
    enabled: Boolean(scenarioId),
  });
}

export function useScenariosQuery(
  baselineId: string,
): UseQueryResult<{ count: number; scenarios: Scenario[] }, Error> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "scenarios", baselineId],
    queryFn: () =>
      axios
        .get<{
          count: number;
          scenarios: Scenario[];
        }>(`/bbe/v1/scenarios`, { params: { baselineId } })
        .then((res) => res.data),
    enabled: Boolean(baselineId),
  });
}

export function useScenarioNamesQuery(baselineId: string): Omit<
  UseQueryResult<{ count: number; scenarios: Scenario[] }, Error>,
  "data"
> & {
  data: string[];
} {
  const query = useScenariosQuery(baselineId);
  const scenarioNames =
    query.data?.scenarios.map((scenario) => scenario.name) ?? [];

  return { ...query, data: scenarioNames };
}

export function useScenarioMutation(): UseMutationResult<
  Scenario,
  Error,
  { scenarioId: string; name?: string; description?: string }
> {
  const axios = useApi();

  return useMutation({
    mutationFn: ({ scenarioId, ...body }) =>
      axios
        .patch<Scenario>(`/bbe/v1/scenarios/${scenarioId}`, body)
        .then((res) => res.data),
  });
}

export function useBaselineRenameMutation(
  baselineId: string,
  { onSuccess, onError }: { onSuccess?: () => void; onError?: () => void } = {},
): UseMutationResult<Baseline, Error, string> {
  const axios = useApi();

  return useMutation({
    mutationFn: (label: string) =>
      axios
        .patch<Baseline>(`/bbe/v1/baselines/${baselineId}`, { label })
        .then((res) => res.data),
    onSuccess,
    onError,
  });
}

export function useAddScenarioMutation(
  baselineId: string,
  { onSuccess }: { onSuccess?: () => void } = {},
): UseMutationResult<Scenario, Error, { name: string; description?: string }> {
  const axios = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  return useMutation({
    mutationFn: (body) =>
      axios
        .post<Scenario>(`/bbe/v1/scenarios`, {
          baselineId,
          ...body,
        })
        .then((res) => res.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["bbe", "scenariosSummary", baselineId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["bbe", "scenarios", baselineId],
      });
      onSuccess?.();
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to add scenario. Please try again.",
      });
    },
  });
}

export function useFetchCarrierOptionsQuery(): UseQueryResult<
  { label: string; value: string }[],
  Error
> {
  const axios = useApi();

  return useQuery({
    queryKey: ["carrier"],
    queryFn: () =>
      axios
        .get<{ carriers: Record<string, string> }>(BBE_CARRIERS_PATH)
        .then((res) =>
          Object.entries(res.data.carriers).map(([key, value]) => ({
            label: value,
            value: key,
          })),
        ),
  });
}

export function useCarrierMapQuery(): UseQueryResult<
  { carriers: Record<string, string> },
  Error
> {
  const axios = useApi();

  return useQuery({
    queryKey: ["bbe", "carrierMap"],
    queryFn: () =>
      axios
        .get<{ carriers: Record<string, string> }>(BBE_CARRIERS_PATH)
        .then((res) => res.data),
  });
}

export function useCreateScenarioEditsMutation(
  scenarioId: string,
): UseMutationResult<ScenarioEdit[], Error, ScenarioEditInput[]> {
  const axios = useApi();

  return useMutation({
    mutationFn: (edits) =>
      axios
        .post<ScenarioEdit[]>(`/bbe/v1/scenarios/${scenarioId}/edits`, {
          edits,
        })
        .then((res) => res.data),
  });
}
